import apiClient from '../../apis';

/**
 * The Impact page's reads (#1516, S16 of #1500). Every number on the page comes from one of these, and nothing
 * is computed here that the server did not release:
 * - `GET /api/usage/weekly` (S15, #1515): workspace admins only, completed weeks only, a total only with at least
 *   three people asking, a breakdown only when every group in it has at least three, never an "Other";
 * - `GET /api/debug/feedback/summary`: the caller's OWN ratings, never anyone else's;
 * - `GET /api/activity`: the timeline as the caller may read it.
 */
export interface UsageFigures { asks: number; askers: number; cost_usd: number; warehouse_bytes: number | null }
export interface UsageGroup extends UsageFigures { value: string }
export interface UsageWeek {
  week: string;
  total: UsageFigures | null;
  withheld: boolean;
  breakdown?: { dimension: string; withheld: boolean; groups: UsageGroup[] };
}
export type UsageDimension = 'channel' | 'route' | 'outcome';
export const IMPACT_DIMENSIONS: UsageDimension[] = ['channel', 'route', 'outcome'];
export const IMPACT_WEEKS = 8;

export async function readUsage(dimension: UsageDimension): Promise<UsageWeek[]> {
  // the client's interceptor has already unwrapped the `{success, data}` envelope
  const { data } = await apiClient.get<{ weeks: UsageWeek[] }>('/api/usage/weekly', { params: { dimension, weeks: IMPACT_WEEKS } });
  return Array.isArray(data?.weeks) ? data.weeks : [];
}

export interface FeedbackSummary { last_7d: { up: number; down: number } }
export async function readOwnFeedback(): Promise<FeedbackSummary | null> {
  const { data } = await apiClient.get<FeedbackSummary>('/api/debug/feedback/summary');
  const last = data?.last_7d;
  return last && Number.isFinite(last.up) && Number.isFinite(last.down) ? { last_7d: { up: last.up, down: last.down } } : null;
}

export interface ActivityItem { id: string; kind: string; event: string; at: string | null; title: string }
export async function readActivity(): Promise<ActivityItem[]> {
  const { data } = await apiClient.get<{ items: ActivityItem[] }>('/api/activity');
  return Array.isArray(data?.items) ? data.items : [];
}

/** The newest completed week the server returned (its own dates label it, so an older week is never called "last week"). */
export function latestWeek(weeks: UsageWeek[]): UsageWeek | null {
  return weeks.length ? weeks.reduce((a, b) => (b.week > a.week ? b : a)) : null;
}

/** A week's breakdown as released: its groups, or null when the server withheld it (or the week's total). */
export function released(week: UsageWeek | null): UsageGroup[] | null {
  if (!week || week.withheld || !week.total || !week.breakdown || week.breakdown.withheld) return null;
  return week.breakdown.groups;
}

const LABELS: Record<string, Record<string, string>> = {
  channel: { web: 'Web', slack: 'Slack', mcp: 'MCP', extension: 'Browser extension' },
  route: {
    catalog: 'What exists', data: 'Data', why: 'Why', knowledge: 'Knowledge', document: 'Document',
    help: 'Help', decline: 'Outside what it answers', prior_work: 'Prior work',
    unsure: 'Asked which was meant', none: 'No route',
  },
  outcome: { answered: 'Answered', asked_back: 'Asked back', refused: 'Refused', failed: 'Failed', stopped: 'Stopped' },
};
export function groupLabel(dimension: UsageDimension, value: string): string {
  return LABELS[dimension]?.[value] ?? value;
}

export function weekLabel(iso: string): string {
  const start = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return iso;
  const end = new Date(start.getTime() + 6 * 86400000);
  const fmt = (d: Date, withMonth: boolean) => d.toLocaleDateString('en-US', withMonth ? { month: 'short', day: 'numeric', timeZone: 'UTC' } : { day: 'numeric', timeZone: 'UTC' });
  return `${fmt(start, true)} to ${fmt(end, end.getUTCMonth() !== start.getUTCMonth())}`;
}

export function money(usd: number): string {
  if (usd > 0 && usd < 0.01) return '<$0.01';
  return `$${usd.toFixed(2)}`;
}

export function bytes(n: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = n;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) { v /= 1000; i += 1; }
  return `${i === 0 ? v : v.toFixed(1)} ${units[i]}`;
}
